import React, { useMemo, useState } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

function DrmtlgyReconciliation() {
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [warehouseLocationId, setWarehouseLocationId] = useState('');
    const [requestedBy, setRequestedBy] = useState('');
    const [approvedBy, setApprovedBy] = useState('');
    const [dryRun, setDryRun] = useState(false);

    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingApprove, setLoadingApprove] = useState(false);
    const [error, setError] = useState('');

    const [requestId, setRequestId] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [summary, setSummary] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [skipped, setSkipped] = useState([]);
    const [executionResult, setExecutionResult] = useState(null);

    const canPreview = useMemo(() => requestedBy.trim().length > 0, [requestedBy]);
    const canApprove = useMemo(
        () => requestId && approvedBy.trim().length > 0 && candidates.length > 0,
        [requestId, approvedBy, candidates.length]
    );

    const handlePreview = async (e) => {
        e.preventDefault();
        if (!canPreview) return;
        setLoadingPreview(true);
        setError('');
        setExecutionResult(null);

        try {
            const res = await axios.post(`${BASE_URL}/api/drmtlgy/reconciliation/preview`, {
                from: from || undefined,
                to: to || undefined,
                warehouseLocationId: warehouseLocationId || undefined,
                requestedBy: requestedBy.trim(),
            });

            setRequestId(res.data.requestId || '');
            setExpiresAt(res.data.expiresAt || '');
            setSummary(res.data.summary || null);
            setCandidates(res.data.candidates || []);
            setSkipped(res.data.skipped || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to build reconciliation preview');
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleApprove = async () => {
        if (!canApprove) return;
        setLoadingApprove(true);
        setError('');
        setExecutionResult(null);

        try {
            const res = await axios.post(`${BASE_URL}/api/drmtlgy/reconciliation/approve/${encodeURIComponent(requestId)}`, {
                approvedBy: approvedBy.trim(),
                dryRun,
            });
            setExecutionResult(res.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Approval failed');
        } finally {
            setLoadingApprove(false);
        }
    };

    return (
        <div className="container mt-4 mb-5">
            <h2 className="mb-3">DRMTLGY Reconciliation</h2>
            <p className="text-muted mb-4">
                Compare Shopify open orders by assigned location against ShipHero pending orders.
                Cancellation mutation runs only after manual approval.
            </p>

            <div className="card mb-4">
                <div className="card-header fw-semibold">1. Preview Mismatch Candidates</div>
                <div className="card-body">
                    <form onSubmit={handlePreview}>
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label">From</label>
                                <input type="date" className="form-control" value={from} onChange={(e) => setFrom(e.target.value)} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">To</label>
                                <input type="date" className="form-control" value={to} onChange={(e) => setTo(e.target.value)} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Warehouse Location ID</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. 63892258871"
                                    value={warehouseLocationId}
                                    onChange={(e) => setWarehouseLocationId(e.target.value)}
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Requested By</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="ops.user"
                                    value={requestedBy}
                                    onChange={(e) => setRequestedBy(e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="mt-3">
                            <button className="btn btn-primary" type="submit" disabled={!canPreview || loadingPreview}>
                                {loadingPreview ? 'Building preview...' : 'Generate Preview'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger">{error}</div>
            )}

            {summary && (
                <div className="card mb-4">
                    <div className="card-header fw-semibold">Preview Summary</div>
                    <div className="card-body">
                        <div><strong>Request ID:</strong> {requestId}</div>
                        <div><strong>Expires At:</strong> {expiresAt ? new Date(expiresAt).toLocaleString() : '-'}</div>
                        <div><strong>Shopify Orders Scanned:</strong> {summary.totalShopifyOrders}</div>
                        <div><strong>ShipHero Orders Scanned:</strong> {summary.totalShipHeroOrders}</div>
                        <div><strong>Candidates:</strong> {summary.cancellationCandidates}</div>
                        <div><strong>Skipped:</strong> {summary.skippedCount}</div>
                    </div>
                </div>
            )}

            {candidates.length > 0 && (
                <div className="card mb-4">
                    <div className="card-header fw-semibold">2. Approve Cancellation Execution</div>
                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label">Approved By</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="manager.user"
                                    value={approvedBy}
                                    onChange={(e) => setApprovedBy(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <div className="form-check mt-4">
                                    <input
                                        id="dryRunCheckbox"
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={dryRun}
                                        onChange={(e) => setDryRun(e.target.checked)}
                                    />
                                    <label htmlFor="dryRunCheckbox" className="form-check-label">
                                        Dry run only (do not execute mutation)
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <button className="btn btn-danger" type="button" disabled={!canApprove || loadingApprove} onClick={handleApprove}>
                                    {loadingApprove ? 'Submitting approval...' : 'Approve and Continue'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {executionResult && (
                <div className={`alert ${executionResult.success ? 'alert-success' : 'alert-warning'} mb-4`}>
                    <div><strong>Request ID:</strong> {executionResult.requestId}</div>
                    <div><strong>Executed:</strong> {String(executionResult.executed)}</div>
                    {'succeeded' in executionResult && <div><strong>Succeeded:</strong> {executionResult.succeeded}</div>}
                    {'failed' in executionResult && <div><strong>Failed:</strong> {executionResult.failed}</div>}
                </div>
            )}

            <div className="card mb-4">
                <div className="card-header fw-semibold">Candidates ({candidates.length})</div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-sm table-striped mb-0">
                            <thead>
                                <tr>
                                    <th>ShipHero Order</th>
                                    <th>Line Item ID</th>
                                    <th>SKU</th>
                                    <th>Qty</th>
                                    <th>Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidates.length ? candidates.map((row, idx) => (
                                    <tr key={`${row.lineItemId}-${idx}`}>
                                        <td>{row.shipheroOrderNumber || '-'}</td>
                                        <td><code>{row.lineItemId}</code></td>
                                        <td>{row.sku || '-'}</td>
                                        <td>{row.quantity}</td>
                                        <td>{row.reason}</td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" className="text-center py-3">No candidates</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-header fw-semibold">Skipped ({skipped.length})</div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0">
                            <thead>
                                <tr>
                                    <th>ShipHero Order</th>
                                    <th>SKU</th>
                                    <th>Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {skipped.length ? skipped.map((row, idx) => (
                                    <tr key={`${row.shipheroOrderNumber}-${idx}`}>
                                        <td>{row.shipheroOrderNumber || '-'}</td>
                                        <td>{row.sku || '-'}</td>
                                        <td>{row.reason || '-'}</td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="3" className="text-center py-3">No skipped records</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DrmtlgyReconciliation;
